import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function InteractiveMapSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'embed';
    const pins = content.pins || [];
    const embedUrl = content.embed_url || '';
    const [activePin, setActivePin] = useState(0);

    if (pins.length === 0 && !embedUrl) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const getItemImage = (item) => {
        if (item.image_folder_id && item.image_file_id && mediaFolders[item.image_folder_id]) {
            const found = mediaFolders[item.image_folder_id].find(f => String(f.id) === String(item.image_file_id));
            if (found) return found.image_url;
        }
        return item.image_url || null;
    };

    const LocationCard = ({ pin, i, isActive }) => {
        const img = getItemImage(pin);
        return (
            <div onClick={() => setActivePin(i)}
                className={`${card} p-5 cursor-pointer transition-all duration-300 hover:-translate-y-0.5 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{
                    transitionDelay: visible ? `${150 + i * 100}ms` : '0ms',
                    borderLeft: isActive ? `3px solid ${s.accent}` : '3px solid transparent',
                }}>
                <div className="flex items-start gap-4">
                    {img && <img src={img} alt={pin.name} className="w-16 h-16 rounded-lg object-cover shrink-0" loading="lazy" />}
                    <div className="min-w-0 flex-1">
                        <h4 className="font-bold text-base" style={{ color: s.text }}>{pin.name}</h4>

                        {pin.address && (
                            <div className="flex items-start gap-1.5 mt-2">
                                <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm" style={{ color: s.textSub }}>{pin.address}</span>
                            </div>
                        )}

                        {pin.phone && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                                <svg className="w-4 h-4 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <a href={`tel:${pin.phone}`} className="text-sm hover:underline transition-colors" style={{ color: s.textSub }}>{pin.phone}</a>
                            </div>
                        )}

                        {pin.hours && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                                <svg className="w-4 h-4 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm" style={{ color: s.textSub }}>{pin.hours}</span>
                            </div>
                        )}

                        <div className="flex items-center gap-3 mt-3">
                            {pin.cta_text && (
                                <a {...resolveLinkProps(pin.cta_link || '#contact')} className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 hover:scale-105"
                                    style={{ backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }}>
                                    {pin.cta_text}
                                </a>
                            )}
                            {pin.lat && pin.lng && (
                                <a href={`https://www.google.com/maps?q=${pin.lat},${pin.lng}`} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-medium hover:underline transition-colors" style={{ color: s.accent }}>
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    Ver mapa
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ── Cards-only variant ──
    if (variant === 'cards-only' || !embedUrl) {
        const cols = config.columns || 3;
        const gridCls = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Ubicaciones'} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                        {pins.map((pin, i) => (
                            <LocationCard key={i} pin={pin} i={i} isActive={activePin === i} />
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── List-map variant ──
    if (variant === 'list-map') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Ubicaciones'} subtitle={content.subtitle} config={config} s={s} />
                    {embedUrl && (
                        <div className="rounded-xl overflow-hidden mb-8 shadow-lg">
                            <iframe src={embedUrl} className="w-full h-80 border-0" allowFullScreen loading="lazy" title="Mapa" />
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {pins.map((pin, i) => (
                            <LocationCard key={i} pin={pin} i={i} isActive={activePin === i} />
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Embed variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Ubicaciones'} subtitle={content.subtitle} config={config} s={s} />
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Map */}
                    <div className="lg:w-3/5">
                        <div className={`rounded-xl overflow-hidden shadow-lg transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            <iframe src={embedUrl} className="w-full h-96 lg:h-[500px] border-0" allowFullScreen loading="lazy" title="Mapa" />
                        </div>
                    </div>

                    {/* Location cards */}
                    <div className="lg:w-2/5 space-y-4 max-h-[500px] overflow-y-auto pr-1">
                        {pins.map((pin, i) => (
                            <LocationCard key={i} pin={pin} i={i} isActive={activePin === i} />
                        ))}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
